import { CRMChannel, useRestApiProvider } from "@jugl-web/rest-api";
import {
  DRAWER_TRANSITION_DURATION_MS,
  InteractiveContainer,
} from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useCXSignupDialog } from "@web-src/modules/cx/components/CXSignupDialog/useCXSignupDialog";
import { useVoiceAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/hooks/useVoiceAccount";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { useInstagramAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/InstagramAccountProvider/useInstagramAccount";
import { useWhatsappAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WhatsappAccountProvider/useWhatsappAccount";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { FC, ReactNode, useCallback, useMemo } from "react";
import { ChannelPreviewPanel } from "../ChannelPreviewPanel";
import { LIVE_CHAT_ENABLE_MUTATION_CACHE_KEY } from "../LiveChatTile/consts";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import emailIllustration from "./assets/email-illustration.png";
import { ReactComponent as EmailIcon } from "./assets/email.svg";
import instagramIllustration from "./assets/instagram-illustration.png";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import liveChatIllustration from "./assets/live-chat-illustration.png";
import { ReactComponent as LiveChatIcon } from "./assets/live-chat.svg";
import twilioIllustration from "./assets/twilio-illustration.png";
import { ReactComponent as TwilioIcon } from "./assets/twilio.svg";
import voiceIllustration from "./assets/voice-illustration.png";
import { ReactComponent as VoiceIcon } from "./assets/voice.svg";
import whatsappIllustration from "./assets/whatsapp-illustration.png";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";

export type DisconnectedChannel =
  | CRMChannel.whatsapp
  | CRMChannel.instagram
  | CRMChannel.twilio
  | CRMChannel.voice
  | CRMChannel.email
  | "live-chat";

type DisconnectedChannelPreviewProps = {
  channel: DisconnectedChannel;
};

export const DisconnectedChannelPreview: FC<
  DisconnectedChannelPreviewProps
> = ({ channel }) => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { isAnonymous } = useAnonymousSession();
  const cxSignupDialog = useCXSignupDialog();
  const { openManageAccountDrawer } = useManageAccountsDrawer();
  const { integrationsApi } = useRestApiProvider();
  const { connectWhatsappAccount, isCreatingAccount: isConnectingWhatsapp } =
    useWhatsappAccount();
  const {
    loginInstagramAccount,
    isConnectingInstagramAccount: isConnectingInstagram,
  } = useInstagramAccount();
  const { createAccount, isCreating: isConnectingVoice } = useVoiceAccount();
  const { isFree, openSubscriptionDrawer } = useSubscriptionV2();
  const isVoiceChannelLocked = channel === CRMChannel.voice && isFree;

  const [updateLiveChatAppConfig, { isLoading: isEnablingLiveChat }] =
    integrationsApi.useUpdateLiveChatAppConfigMutation({
      fixedCacheKey: LIVE_CHAT_ENABLE_MUTATION_CACHE_KEY,
    });

  const isConnecting = useMemo(() => {
    switch (channel) {
      case CRMChannel.whatsapp:
        return isConnectingWhatsapp;
      case CRMChannel.instagram:
        return isConnectingInstagram;
      case CRMChannel.voice:
        return isConnectingVoice;
      case "live-chat":
        return isEnablingLiveChat;
      default:
        return false;
    }
  }, [
    channel,
    isConnectingInstagram,
    isConnectingVoice,
    isConnectingWhatsapp,
    isEnablingLiveChat,
  ]);

  const handleEnableLiveChat = useCallback(async () => {
    if (isEnablingLiveChat) {
      return;
    }

    await updateLiveChatAppConfig({
      entityId,
      enabled: true,
      meta: {
        successMessage: t({
          id: "cx-ai-agents-page.live-chat-has-been-enabled",
          defaultMessage: "Live chat has been enabled",
        }),
      },
    });
  }, [entityId, isEnablingLiveChat, t, updateLiveChatAppConfig]);

  const handleConnect = useCallback(() => {
    if (isConnecting) {
      return;
    }

    if (isAnonymous) {
      cxSignupDialog?.open();
      return;
    }

    if (isVoiceChannelLocked) {
      openSubscriptionDrawer();
      return;
    }

    switch (channel) {
      case CRMChannel.whatsapp:
        connectWhatsappAccount();
        break;
      case CRMChannel.instagram:
        loginInstagramAccount();
        break;
      case CRMChannel.twilio:
        openManageAccountDrawer({ name: "twilioAccount" });
        break;
      case CRMChannel.voice:
        createAccount();
        break;
      case CRMChannel.email:
        openManageAccountDrawer({ name: "emailSetup" });
        break;
      case "live-chat":
        openManageAccountDrawer({ name: "liveChatAppAccount" });
        setTimeout(handleEnableLiveChat, DRAWER_TRANSITION_DURATION_MS + 50);
        break;
      default:
        break;
    }
  }, [
    channel,
    connectWhatsappAccount,
    createAccount,
    cxSignupDialog,
    handleEnableLiveChat,
    isAnonymous,
    isConnecting,
    isVoiceChannelLocked,
    loginInstagramAccount,
    openManageAccountDrawer,
    openSubscriptionDrawer,
  ]);

  const connectLabel = t({
    id: "common.connect",
    defaultMessage: "Connect",
  });

  const previewAfterConnectionSubtitle = t({
    id: "cx-ai-agents-page.channel-preview-available-after-connection",
    defaultMessage: "* Channel preview available after account connection",
  });

  const highlightedText = (text: ReactNode) => (
    <InteractiveContainer
      isDisabled={isConnecting}
      onClick={handleConnect}
      className="inline font-medium text-primary-800 underline"
    >
      {text}
    </InteractiveContainer>
  );

  const content = (() => {
    switch (channel) {
      case CRMChannel.email:
        return {
          icon: (
            <EmailIcon className="drop-shadow-[-10.125px_11.25px_13.275px_rgba(60,60,60,0.16)]" />
          ),
          title: t({
            id: "cx-ai-agents-page.connect-email-inbox-in-seconds",
            defaultMessage: "Connect Email inbox in seconds",
          }),
          subtitle: t({
            id: "cx-ai-agents-page.email-channel-preview-unavailable",
            defaultMessage: "* Preview of Email channel is not available",
          }),
          actionLabel: connectLabel,
          bannerContent: t(
            {
              id: "cx-ai-agents-page.install-email-account-banner",
              defaultMessage:
                "Install your <highlightedText>Email account</highlightedText> to securely connect your business inbox. Send, receive, and manage customer emails from Jugl, or let AI to handle the flow",
            },
            { highlightedText }
          ),
          illustration: (
            <img
              src={emailIllustration}
              alt=""
              className="h-auto w-41.75 shrink-0 object-contain"
            />
          ),
        };
      case CRMChannel.whatsapp:
        return {
          icon: (
            <WhatsappIcon className="drop-shadow-[-10.125px_11.25px_13.275px_rgba(60,60,60,0.16)]" />
          ),
          title: t({
            id: "cx-ai-agents-page.whatsapp-connect-description",
            defaultMessage: "Reply faster with WhatsApp chat",
          }),
          subtitle: previewAfterConnectionSubtitle,
          actionLabel: connectLabel,
          bannerContent: t(
            {
              id: "cx-ai-agents-page.whatsapp-connect-banner",
              defaultMessage:
                "Start WhatsApp business chatting with customers instantly. Sign in to your <highlightedText>Meta Business account</highlightedText> and choose the WhatsApp number you want to connect. Easy as that!",
            },
            { highlightedText }
          ),
          illustration: (
            <img
              src={whatsappIllustration}
              alt=""
              className="h-auto w-41.75 shrink-0 object-contain"
            />
          ),
        };
      case CRMChannel.instagram:
        return {
          icon: (
            <InstagramIcon className="drop-shadow-[-10.125px_11.25px_13.275px_rgba(60,60,60,0.16)]" />
          ),
          title: t({
            id: "cx-ai-agents-page.instagram-connect-description",
            defaultMessage: "Elevate your marketing with Instagram",
          }),
          subtitle: previewAfterConnectionSubtitle,
          actionLabel: connectLabel,
          bannerContent: t(
            {
              id: "cx-ai-agents-page.instagram-connect-banner",
              defaultMessage:
                "Your AI can instantly reply to Instagram DMs, answer questions, and qualify leads. Just sign in to your <highlightedText>Meta Business account</highlightedText> and select the Instagram Business profile you want to connect.",
            },
            { highlightedText }
          ),
          illustration: (
            <img
              src={instagramIllustration}
              alt=""
              className="h-auto w-41.75 shrink-0 object-contain"
            />
          ),
        };
      case CRMChannel.twilio:
        return {
          icon: (
            <TwilioIcon className="drop-shadow-[-10.125px_11.25px_13.275px_rgba(60,60,60,0.16)]" />
          ),
          title: t({
            id: "cx-ai-agents-page.communicate-via-sms-with-twilio",
            defaultMessage: "Communicate via SMS with Twilio",
          }),
          subtitle: previewAfterConnectionSubtitle,
          actionLabel: connectLabel,
          bannerContent: t(
            {
              id: "cx-ai-agents-page.twilio-connect-banner",
              defaultMessage:
                "Securely connect your <highlightedText>Twilio account</highlightedText>, select the phone number you want to use, and start sending SMS conversations from Jugl.",
            },
            { highlightedText }
          ),
          illustration: (
            <img
              src={twilioIllustration}
              alt=""
              className="h-auto w-41.75 shrink-0 object-contain"
            />
          ),
        };
      case "live-chat":
        return {
          icon: (
            <LiveChatIcon className="drop-shadow-[-10.125px_11.25px_13.275px_rgba(60,60,60,0.16)]" />
          ),
          title: t({
            id: "cx-ai-agents-page.start-conversations-with-chat-link",
            defaultMessage: "Start Conversations with a Chat Link",
          }),
          subtitle: t({
            id: "cx-ai-agents-page.channel-preview-available-after-enabling",
            defaultMessage: "* Channel preview available after enabling",
          }),
          actionLabel: t({
            id: "cx-ai-agents-page.setup",
            defaultMessage: "Setup",
          }),
          bannerContent: t(
            {
              id: "cx-ai-agents-page.live-chat-connect-banner",
              defaultMessage:
                "<highlightedText>Customise and create</highlightedText> public chat link in seconds and let customers message your business from any device - no website required.",
            },
            { highlightedText }
          ),
          illustration: (
            <img
              src={liveChatIllustration}
              alt=""
              className="h-auto w-41.75 shrink-0 object-contain"
            />
          ),
        };
      case CRMChannel.voice:
        return {
          icon: (
            <VoiceIcon className="drop-shadow-[-10.125px_11.25px_13.275px_rgba(60,60,60,0.16)]" />
          ),
          title: t({
            id: "cx-ai-agents-page.routine-calls-handled-by-ai",
            defaultMessage: "Routine calls handled by AI",
          }),
          subtitle: t({
            id: "cx-ai-agents-page.phone-calls-preview-unavailable",
            defaultMessage: "* Preview is not available",
          }),
          actionLabel: isVoiceChannelLocked
            ? t({
                id: "client-support-page.upgrade-plan",
                defaultMessage: "Upgrade Plan",
              })
            : t({
                id: "common.enable",
                defaultMessage: "Enable",
              }),
          bannerContent: t({
            id: "cx-ai-agents-page.voice-connect-banner",
            defaultMessage:
              "Get your free Jugl phone number, set calls transferring and AI Agent instructions and let it handle the rest ⚡",
          }),
          illustration: (
            <img
              src={voiceIllustration}
              alt=""
              className="h-auto w-17 shrink-0 object-contain"
            />
          ),
        };
      default:
        return null;
    }
  })();

  if (!content) {
    return null;
  }

  return (
    <ChannelPreviewPanel
      icon={content.icon}
      title={content.title}
      subtitle={content.subtitle}
      badge={
        isVoiceChannelLocked ? (
          <div className="flex h-10.5 w-max items-center gap-3 rounded-2xl bg-linear-to-r from-[#FBF6FF] to-[#F1F8FF] px-3">
            <InfoIcon />
            <span className="font-secondary text-xs font-medium text-dark">
              {t({
                id: "cx-ai-agents-page.available-for-paid-plans-only",
                defaultMessage: "Available for Paid Plans only",
              })}
            </span>
          </div>
        ) : undefined
      }
      action={{
        label: content.actionLabel,
        onClick: handleConnect,
        iconEnd: isVoiceChannelLocked ? <PricingIcon /> : <ArrowIcon />,
        isDisabled: isConnecting,
      }}
      bannerContent={content.bannerContent}
      bannerIllustration={content.illustration}
    />
  );
};
